import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { UserMinus, Plus, X, ShieldCheck, FileSignature, Package, DollarSign, CheckCircle2, MessageSquare, ArrowRight, AlertTriangle, ShieldOff, HeartPulse } from "lucide-react";

const statusBadge = (s) => {
  const map = { requested: "sw-badge-pending", in_progress: "sw-badge-gold", completed: "sw-badge-approved", rejected: "sw-badge-rejected" };
  return <span className={`sw-badge ${map[s] || "sw-badge-neutral"}`}>{s.replace("_", " ")}</span>;
};

function NewTerminationModal({ users, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ user_id: "", type: "voluntary", reason: "", last_day: "", replace: false, notes: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/terminations", form);
      onSaved();
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[560px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <h3 className="sw-heading text-[20px]">{t("offb.new_termination")}</h3>
          <button onClick={onClose} className="text-[#747474]"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="sw-label">Employee *</label>
            <select required value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} className="sw-input">
              <option value="">— Select —</option>
              {users.filter((u) => u.role !== "admin" && u.active !== false).map((u) => <option key={u.id} value={u.id}>{u.name} · {u.job_title} · {u.contract_type?.replace("_", "·")}</option>)}
            </select>
          </div>
          <div>
            <label className="sw-label">Type *</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="sw-input">
              <option value="voluntary">Voluntary (resignation)</option>
              <option value="involuntary">Involuntary (let go)</option>
            </select>
          </div>
          <div>
            <label className="sw-label">Last day *</label>
            <input required type="date" value={form.last_day} onChange={(e) => setForm({ ...form, last_day: e.target.value })} className="sw-input" />
          </div>
          <div className="md:col-span-2">
            <label className="sw-label">Reason *</label>
            <textarea required rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="sw-input" />
          </div>
          <div className="md:col-span-2">
            <label className="sw-label">Notes</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="sw-input" />
          </div>
          <label className="inline-flex items-center gap-2 text-[13px]">
            <input type="checkbox" checked={form.replace} onChange={(e) => setForm({ ...form, replace: e.target.checked })} className="accent-[#475569]" />
            {t("offb.open_vacancy")}
          </label>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button data-testid="save-term-btn" type="submit" disabled={saving} className="sw-btn-primary">{saving ? "..." : t("offb.submit_request")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TerminationDetailModal({ termination, onClose, onUpdated }) {
  const { t: tx } = useI18n();
  const [working, setWorking] = useState(false);
  const [eiForm, setEiForm] = useState({ overall_rating: 5, would_recommend: true, feedback: "", reason_leaving: "" });
  const [showEI, setShowEI] = useState(false);
  const [eqChoice, setEqChoice] = useState({});

  const act = async (fn) => {
    setWorking(true);
    try { await fn(); await onUpdated(); }
    catch (err) { alert(err?.response?.data?.detail || "Error"); }
    finally { setWorking(false); }
  };

  const approve = () => act(() => api.post(`/terminations/${termination.id}/approve`, { decision: "approved", comment: "Approved" }));
  const calcSeverance = () => act(() => api.post(`/terminations/${termination.id}/severance`));
  const frankcrum = () => act(() => api.post(`/terminations/${termination.id}/frankcrum-unenroll`));
  const docusign = () => act(() => api.post(`/terminations/${termination.id}/docusign-termination`));
  const revokeAccess = () => act(() => api.post(`/terminations/${termination.id}/revoke-access`, { systems: ["email", "vpn", "badge", "github"], notes: "All access revoked" }));
  const cobraNotice = () => act(() => api.post(`/terminations/${termination.id}/cobra-notice`));
  const complete = () => { if (window.confirm(tx("offb.finalize_confirm"))) act(() => api.post(`/terminations/${termination.id}/complete`)); };
  const submitEI = async (e) => {
    e.preventDefault();
    setWorking(true);
    try {
      await api.post(`/terminations/${termination.id}/exit-interview`, eiForm);
      setShowEI(false);
      await onUpdated();
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
    finally { setWorking(false); }
  };
  const markEquipment = async (item) => {
    setWorking(true);
    try {
      await api.post(`/terminations/${termination.id}/equipment-return`, { item, condition: eqChoice[item] || "good", notes: null });
      await onUpdated();
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
    finally { setWorking(false); }
  };

  const t = termination;
  const allEquipReturned = (t.equipment_return || []).every((i) => i.returned);
  const requiredKeys = ["exit_interview", "equipment_return", "final_payout", "docusign_termination"];
  if (t.user_contract_type === "CLT_FrankCrum") {
    requiredKeys.push("frankcrum_unenroll", "cobra_notice");
  }
  const canComplete = t.status === "in_progress" && t.tasks.every((tk) => !requiredKeys.includes(tk.key) || tk.status === "completed");

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[820px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#475569]">{t.type} · {t.user_contract_type?.replace("_", "·")}</div>
            <h3 className="sw-heading text-[22px] mt-1">{t.user_name}</h3>
            <div className="text-[13px] text-[#747474]">Last day: {t.last_day} · Reason: {t.reason}</div>
          </div>
          <div className="flex items-center gap-2">
            {statusBadge(t.status)}
            <button onClick={onClose} className="text-[#747474]"><X size={20} /></button>
          </div>
        </div>

        {/* Status actions */}
        {t.status === "requested" && (
          <div className="bg-[#fef3c7] border border-[#f59e0b] rounded-[12px] p-4 mb-5 flex items-center justify-between">
            <div>
              <div className="font-medium text-[14px] text-[#92400e]">{tx("offb.awaiting_approval")}</div>
              <div className="text-[12px] text-[#747474]">{tx("offb.requested_by")} {t.requested_by_name}</div>
            </div>
            <button data-testid="approve-term-btn" onClick={approve} disabled={working} className="sw-btn-primary !bg-[#008932] hover:!bg-[#0b430b]"><CheckCircle2 size={14} /> {tx("common.approve")}</button>
          </div>
        )}

        {/* Final payout */}
        {t.final_payout && (
          <div className="sw-card !p-4 mb-4 bg-[#e2e8f0]/30">
            <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-2"><DollarSign size={11} className="inline" /> Final payout</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><div className="text-[10px] uppercase text-[#747474]">Severance</div><div className="font-semibold">${t.final_payout.severance_amount}</div></div>
              <div><div className="text-[10px] uppercase text-[#747474]">PTO ({t.final_payout.pto_payout_days}d)</div><div className="font-semibold">${t.final_payout.pto_payout_amount}</div></div>
              <div><div className="text-[10px] uppercase text-[#747474]">Vacation ({t.final_payout.vacation_payout_days}d)</div><div className="font-semibold">${t.final_payout.vacation_payout_amount}</div></div>
              <div><div className="text-[10px] uppercase text-[#747474]">{tx("common.total")}</div><div className="font-semibold text-[#475569] text-[18px]">${t.final_payout.total_amount}</div></div>
            </div>
            {t.severance && <div className="text-[11px] text-[#747474] mt-2">{t.severance.notes}</div>}
          </div>
        )}

        {/* Action grid */}
        {t.status === "in_progress" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
            <button onClick={() => setShowEI(true)} disabled={working || !!t.exit_interview} className={`sw-card !p-4 text-left ${t.exit_interview ? "opacity-60" : "hover:border-[#475569]"}`}>
              <div className="flex items-center gap-2 mb-1"><MessageSquare size={16} className="text-[#475569]" /><span className="font-medium text-[14px]">{tx("offb.exit_interview")}</span></div>
              <div className="text-[12px] text-[#747474]">{t.exit_interview ? `★ ${t.exit_interview.overall_rating}/5 done` : "Conduct interview"}</div>
            </button>
            <button data-testid="severance-btn" onClick={calcSeverance} disabled={working || !!t.severance} className={`sw-card !p-4 text-left ${t.severance ? "opacity-60" : "hover:border-[#475569]"}`}>
              <div className="flex items-center gap-2 mb-1"><DollarSign size={16} className="text-[#475569]" /><span className="font-medium text-[14px]">Severance + payout</span></div>
              <div className="text-[12px] text-[#747474]">{t.severance ? "Calculated" : "Calculate amounts"}</div>
            </button>
            {t.user_contract_type === "CLT_FrankCrum" && (
              <button data-testid="frankcrum-btn" onClick={frankcrum} disabled={working || !!t.frankcrum_unenroll} className={`sw-card !p-4 text-left ${t.frankcrum_unenroll ? "opacity-60" : "hover:border-[#475569]"}`}>
                <div className="flex items-center gap-2 mb-1"><ShieldCheck size={16} className="text-[#475569]" /><span className="font-medium text-[14px]">{tx("offb.frankcrum_unenroll")}</span></div>
                <div className="text-[12px] text-[#747474]">{t.frankcrum_unenroll ? `Sent · ${t.frankcrum_unenroll.reference}` : "Notify payroll provider (MOCKED)"}</div>
              </button>
            )}
            <button data-testid="docusign-term-btn" onClick={docusign} disabled={working || !!t.docusign_termination} className={`sw-card !p-4 text-left ${t.docusign_termination ? "opacity-60" : "hover:border-[#475569]"}`}>
              <div className="flex items-center gap-2 mb-1"><FileSignature size={16} className="text-[#475569]" /><span className="font-medium text-[14px]">{tx("offb.docusign_term")}</span></div>
              <div className="text-[12px] text-[#747474]">{t.docusign_termination ? `Sent · ${t.docusign_termination.envelope_id}` : "Send termination letter (MOCKED)"}</div>
            </button>
            <button data-testid="revoke-access-btn" onClick={revokeAccess} disabled={working || !!t.access_revoke} className={`sw-card !p-4 text-left ${t.access_revoke ? "opacity-60" : "hover:border-[#475569]"}`}>
              <div className="flex items-center gap-2 mb-1"><ShieldOff size={16} className="text-[#475569]" /><span className="font-medium text-[14px]">{tx("offb.revoke_it")}</span></div>
              <div className="text-[12px] text-[#747474]">{t.access_revoke ? `${tx("offb.revoked")} ${t.access_revoke.systems?.join(", ")}` : "Email, VPN, badge, GitHub..."}</div>
            </button>
            {t.user_contract_type === "CLT_FrankCrum" && (
              <button data-testid="cobra-btn" onClick={cobraNotice} disabled={working || !!t.cobra_notice} className={`sw-card !p-4 text-left ${t.cobra_notice ? "opacity-60" : "hover:border-[#475569]"}`}>
                <div className="flex items-center gap-2 mb-1"><HeartPulse size={16} className="text-[#475569]" /><span className="font-medium text-[14px]">COBRA notice</span></div>
                <div className="text-[12px] text-[#747474]">{t.cobra_notice ? `Sent · ${t.cobra_notice.reference}` : "Send benefits continuation (US)"}</div>
              </button>
            )}
          </div>
        )}

        {/* Equipment return */}
        {(t.equipment_return || []).length > 0 && (
          <div className="sw-card !p-4 mb-4">
            <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-3"><Package size={11} className="inline" /> Equipment return ({(t.equipment_return || []).filter((i) => i.returned).length}/{(t.equipment_return || []).length})</div>
            <div className="flex flex-col gap-2">
              {t.equipment_return.map((it, i) => (
                <div key={i} className={`flex items-center gap-3 p-2 rounded-[10px] ${it.returned ? "bg-[#b8e6cd]/40" : "bg-[#f6f6f6]"}`}>
                  {it.returned ? <CheckCircle2 size={16} className="text-[#008932]" /> : <Package size={16} className="text-[#747474]" />}
                  <div className="flex-1 text-[13px]">{it.item}</div>
                  {!it.returned && t.status === "in_progress" && (
                    <>
                      <select value={eqChoice[it.item] || "good"} onChange={(e) => setEqChoice({ ...eqChoice, [it.item]: e.target.value })} className="sw-input !py-1 !px-2 !text-[12px] w-[110px]">
                        <option value="good">Good</option><option value="damaged">Damaged</option><option value="missing">Missing</option>
                      </select>
                      <button onClick={() => markEquipment(it.item)} disabled={working} className="sw-btn-secondary !px-3 !py-1 !text-[11px]">Mark returned</button>
                    </>
                  )}
                  {it.returned && <span className="sw-badge sw-badge-approved !text-[10px]">{it.condition}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tasks checklist */}
        <div className="sw-card !p-4 mb-4">
          <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-3">Offboarding tasks</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {t.tasks.map((tk, i) => {
              const isCompleted = tk.status === "completed";
              const toggle = async () => {
                setWorking(true);
                try {
                  if (isCompleted) {
                    await api.post(`/terminations/${t.id}/tasks/${tk.key}/reopen`);
                  } else {
                    await api.post(`/terminations/${t.id}/tasks/${tk.key}/complete`);
                  }
                  await onUpdated();
                } catch (err) { alert(err?.response?.data?.detail || "Error"); }
                finally { setWorking(false); }
              };
              return (
                <button
                  key={i}
                  type="button"
                  data-testid={`offb-task-${tk.key}`}
                  onClick={toggle}
                  disabled={working || t.status !== "in_progress"}
                  className={`flex items-center gap-2 text-[13px] p-2 rounded-[8px] text-left transition-colors ${isCompleted ? "bg-[#b8e6cd]/40 text-[#0b430b]" : "bg-[#f6f6f6] text-[#323232] hover:bg-[#e2e8f0]"} ${t.status !== "in_progress" ? "cursor-default" : "cursor-pointer"}`}
                >
                  {isCompleted ? <CheckCircle2 size={14} className="text-[#008932] shrink-0" /> : <div className="w-3 h-3 border-2 border-[#d9d9d9] rounded-full shrink-0"></div>}
                  <span className={`flex-1 ${isCompleted ? "line-through" : ""}`}>{tk.title}</span>
                  <span className="text-[10px] text-[#747474]">{tk.category}</span>
                </button>
              );
            })}
          </div>
          {t.status === "in_progress" && (
            <div className="text-[11px] text-[#94a3b8] mt-2 italic">{tx("offb.click_toggle")}</div>
          )}
        </div>

        {/* Complete button */}
        {canComplete && (
          <div className="flex justify-end">
            <button data-testid="complete-term-btn" onClick={complete} disabled={working} className="sw-btn-primary !bg-[#0b430b]"><CheckCircle2 size={14} /> {tx("offb.finalize_btn")}</button>
          </div>
        )}

        {/* Exit interview modal */}
        {showEI && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-[20px] max-w-[520px] w-full p-6 shadow-xl">
              <div className="flex justify-between items-center mb-4"><h3 className="sw-heading text-[18px]">Exit interview</h3><button onClick={() => setShowEI(false)}><X size={18} /></button></div>
              <form onSubmit={submitEI} className="flex flex-col gap-3">
                <div>
                  <label className="sw-label">Overall experience (1-5)</label>
                  <select value={eiForm.overall_rating} onChange={(e) => setEiForm({ ...eiForm, overall_rating: parseInt(e.target.value) })} className="sw-input">
                    <option value="1">1 · Very poor</option><option value="2">2 · Poor</option><option value="3">3 · Neutral</option><option value="4">4 · Good</option><option value="5">5 · Excellent</option>
                  </select>
                </div>
                <label className="inline-flex items-center gap-2 text-[13px]"><input type="checkbox" checked={eiForm.would_recommend} onChange={(e) => setEiForm({ ...eiForm, would_recommend: e.target.checked })} className="accent-[#475569]" />{tx("offb.would_recommend")}</label>
                <div><label className="sw-label">{tx("offb.primary_reason")}</label><input value={eiForm.reason_leaving} onChange={(e) => setEiForm({ ...eiForm, reason_leaving: e.target.value })} className="sw-input" /></div>
                <div><label className="sw-label">{tx("offb.feedback_sug")}</label><textarea rows={3} value={eiForm.feedback} onChange={(e) => setEiForm({ ...eiForm, feedback: e.target.value })} className="sw-input" /></div>
                <div className="flex justify-end gap-2"><button type="button" onClick={() => setShowEI(false)} className="sw-btn-secondary">{tx("common.cancel")}</button><button type="submit" disabled={working} className="sw-btn-primary">{tx("common.save")}</button></div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Offboarding() {
  const { t } = useI18n();
  const [terms, setTerms] = useState([]);
  const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [detail, setDetail] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");

  const load = async () => {
    const params = filterStatus !== "all" ? `?status=${filterStatus}` : "";
    const [t, u] = await Promise.all([api.get(`/terminations${params}`), api.get("/users")]);
    setTerms(t.data); setUsers(u.data);
  };
  useEffect(() => { load(); }, [filterStatus]); // eslint-disable-line

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center"><UserMinus size={22} /></div>
          <div>
            <h1 className="sw-heading text-[28px]">{t("offb.title")}</h1>
            <p className="text-[14px] text-[#747474]">{t("offb.cases_summary", { n: terms.length, m: terms.filter((x) => x.status === "in_progress").length })}</p>
          </div>
        </div>
        <button data-testid="new-term-btn" onClick={() => setShowNew(true)} className="sw-btn-primary"><Plus size={14} /> {t("offb.new_termination")}</button>
      </div>

      <div className="flex gap-2">
        {["all", "requested", "in_progress", "completed", "rejected"].map((s) => (
          <button key={s} onClick={() => setFilterStatus(s)} className={`px-4 py-2 rounded-[12px] text-[12px] font-medium ${filterStatus === s ? "bg-[#475569] text-white" : "bg-white text-[#747474] border border-[#eeeded]"}`}>
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      {terms.length === 0 ? (
        <div className="sw-card text-center py-12 text-[#747474]">{t("offb.none")}</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {terms.map((t) => {
            const equipDone = (t.equipment_return || []).filter((i) => i.returned).length;
            const equipTotal = (t.equipment_return || []).length;
            const tasksDone = t.tasks.filter((tk) => tk.status === "completed").length;
            return (
              <button key={t.id} data-testid={`term-card-${t.id}`} onClick={() => setDetail(t)} className="sw-card text-left hover:border-[#475569]">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-[#475569]">{t.type} · {t.user_contract_type?.replace("_", "·")}</div>
                    <div className="font-medium text-[16px] text-[#323232] mt-1">{t.user_name}</div>
                    <div className="text-[12px] text-[#747474]">Last day: {t.last_day}</div>
                  </div>
                  {statusBadge(t.status)}
                </div>
                <div className="text-[13px] text-[#323232] italic mb-3">&ldquo;{t.reason}&rdquo;</div>
                <div className="grid grid-cols-3 gap-2 text-[11px]">
                  <div className="bg-[#f6f6f6] rounded-[8px] p-2 text-center"><div className="text-[#747474]">Tasks</div><div className="font-semibold">{tasksDone}/{t.tasks.length}</div></div>
                  <div className="bg-[#f6f6f6] rounded-[8px] p-2 text-center"><div className="text-[#747474]">Equipment</div><div className="font-semibold">{equipDone}/{equipTotal}</div></div>
                  <div className="bg-[#f6f6f6] rounded-[8px] p-2 text-center"><div className="text-[#747474]">Payout</div><div className="font-semibold text-[#475569]">{t.final_payout ? `$${t.final_payout.total_amount}` : "—"}</div></div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {showNew && <NewTerminationModal users={users} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {detail && (
        <TerminationDetailModal
          termination={detail}
          onClose={() => setDetail(null)}
          onUpdated={async () => {
            await load();
            const fresh = (await api.get(`/terminations/${detail.id}`)).data;
            setDetail(fresh);
          }}
        />
      )}
    </div>
  );
}
